import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import type {
  Campaign,
  CampaignChannel,
  CampaignFormData,
  CampaignRecipient,
  CampaignReply,
  CampaignStatus,
  RecipientStatus,
} from './types';
import type { ContactList, ContactListMember } from '../ContactLists/types';
import { listContactLists, listMembers as listListMembers } from '../ContactLists/actions';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import * as api from './actions';
import classes from './campaigns.module.css';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  ssoToken: string;
  companyId: string;
}

// ---------------------------------------------------------------------------
// View enum
// ---------------------------------------------------------------------------

type View = 'list' | 'create' | 'edit' | 'detail';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const statusClass = (status: CampaignStatus): string => {
  const map: Record<CampaignStatus, string> = {
    draft: classes.badgeDraft,
    sending: classes.badgeSending,
    completed: classes.badgeCompleted,
    cancelled: classes.badgeCancelled,
  };
  return `${classes.badge} ${map[status] || ''}`;
};

const channelClass = (channel: CampaignChannel): string => {
  const map: Record<CampaignChannel, string> = {
    sms: classes.channelSms,
    email: classes.channelEmail,
  };
  return `${classes.channelBadge} ${map[channel] || ''}`;
};

const recipientStatusClass = (status: RecipientStatus): string => {
  const map: Record<RecipientStatus, string> = {
    pending: classes.recipientPending,
    queued: classes.recipientQueued,
    sent: classes.recipientSent,
    delivered: classes.recipientDelivered,
    failed: classes.recipientFailed,
    bounced: classes.recipientBounced,
    unsubscribed: classes.recipientUnsubscribed,
  };
  return `${classes.recipientBadge} ${map[status] || ''}`;
};

const emptyFormData: CampaignFormData = {
  name: '',
  channel: 'email',
  subject: '',
  body: '',
  html: '',
  contact_filter: {},
};

// ---------------------------------------------------------------------------
// Campaign Form
// ---------------------------------------------------------------------------

interface CampaignFormProps {
  initial?: CampaignFormData;
  contactLists: ContactList[];
  companyId: string;
  ssoToken: string;
  onSubmit: (data: CampaignFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  saving: boolean;
}

function CampaignForm({ initial, contactLists, companyId, ssoToken, onSubmit, onCancel, submitLabel, saving }: CampaignFormProps) {
  const [form, setForm] = useState<CampaignFormData>(initial || emptyFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // List member preview state
  const [listMembers, setListMembers] = useState<ContactListMember[]>([]);
  const [listMemberPage, setListMemberPage] = useState(1);
  const [listMemberMeta, setListMemberMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [loadingMembers, setLoadingMembers] = useState(false);
  const memberMountedRef = useRef(true);

  useEffect(() => {
    memberMountedRef.current = true;
    return () => { memberMountedRef.current = false; };
  }, []);

  const loadListMembers = useCallback(async (listId: number, page: number) => {
    setLoadingMembers(true);
    try {
      const res = await listListMembers(companyId, listId, ssoToken, page);
      if (!memberMountedRef.current) return;
      setListMembers(res.data);
      setListMemberMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setListMemberPage(page);
    } catch {
      if (!memberMountedRef.current) return;
      setListMembers([]);
    } finally {
      if (memberMountedRef.current) setLoadingMembers(false);
    }
  }, [companyId, ssoToken]);

  // Load members when list_id changes
  useEffect(() => {
    if (form.contact_filter.list_id) {
      loadListMembers(form.contact_filter.list_id, 1);
    } else {
      setListMembers([]);
      setListMemberMeta({ last_page: 1, total: 0 });
      setListMemberPage(1);
    }
  }, [form.contact_filter.list_id, loadListMembers]);

  const handleChange = useCallback((field: keyof CampaignFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleChannelChange = useCallback((channel: CampaignChannel) => {
    setForm((prev) => ({ ...prev, channel }));
  }, []);

  const handleListIdChange = useCallback((listId: string) => {
    const num = Number(listId);
    const parsed = listId && !Number.isNaN(num) ? num : undefined;
    setForm((prev) => ({
      ...prev,
      contact_filter: {
        ...prev.contact_filter,
        list_id: parsed,
      },
    }));
  }, []);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.body.trim()) errs.body = 'Message body is required';
    if (form.channel === 'email' && !form.subject.trim()) errs.subject = 'Subject is required for email campaigns';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit(form);
  }, [form, validate, onSubmit]);

  return (
    <form onSubmit={handleSubmit} className={classes.formCard}>
      <div className={classes.formRow}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Campaign Name</label>
          <input
            type="text"
            className={classes.formInput}
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Spring Follow-up"
          />
          {errors.name && <div className={classes.formError}>{errors.name}</div>}
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Channel</label>
          <select
            className={classes.formSelect}
            value={form.channel}
            onChange={(e) => handleChannelChange(e.target.value as CampaignChannel)}
          >
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
      </div>

      {form.channel === 'email' && (
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Subject</label>
          <input
            type="text"
            className={classes.formInput}
            value={form.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            placeholder="Email subject line"
          />
          {errors.subject && <div className={classes.formError}>{errors.subject}</div>}
        </div>
      )}

      <div className={classes.formGroup}>
        <label className={classes.formLabel}>
          {form.channel === 'sms' ? 'Message' : 'Body (plain text)'}
        </label>
        <textarea
          className={classes.formTextarea}
          value={form.body}
          onChange={(e) => handleChange('body', e.target.value)}
          placeholder={form.channel === 'sms' ? 'SMS message content...' : 'Plain text email body...'}
        />
        {errors.body && <div className={classes.formError}>{errors.body}</div>}
        {form.channel === 'sms' && (
          <div className={classes.formHint}>{form.body.length} / 160 characters</div>
        )}
      </div>

      {form.channel === 'email' && (
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>HTML Body (optional)</label>
          <textarea
            className={classes.formTextarea}
            value={form.html}
            onChange={(e) => handleChange('html', e.target.value)}
            placeholder="<html>...</html>"
            style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
          <div className={classes.formHint}>Optional HTML version of the email. If empty, the plain text body will be used.</div>
        </div>
      )}

      {/* Contact Filter - Contact List */}
      {contactLists.length > 0 && (
        <div className={classes.formGroup}>
          <label className={classes.formLabel}>Contact List (optional)</label>
          <select
            className={classes.formSelect}
            value={form.contact_filter.list_id ?? ''}
            onChange={(e) => handleListIdChange(e.target.value)}
          >
            <option value="">No list selected</option>
            {contactLists.map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.name} ({cl.members_count} member{cl.members_count !== 1 ? 's' : ''})
              </option>
            ))}
          </select>
          <div className={classes.formHint}>Target contacts from a saved list.</div>

          {/* List member preview */}
          {form.contact_filter.list_id && (
            <div className={classes.listPreview}>
              {loadingMembers ? (
                <div className={classes.listPreviewLoading}>
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span>Loading list members...</span>
                </div>
              ) : listMembers.length === 0 ? (
                <div className={classes.listPreviewEmpty}>No members in this list.</div>
              ) : (
                <>
                  <div className={classes.listPreviewTableWrapper}>
                    <table className={`table table-sm ${classes.table} ${classes.listPreviewTable}`}>
                      <thead>
                        <tr>
                          <th>Contact</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listMembers.map((m) => (
                          <tr key={m.id}>
                            <td>{m.contact?.display_name || m.contact_id}</td>
                            <td>{m.contact?.email || '\u2014'}</td>
                            <td>{m.contact?.phone || '\u2014'}</td>
                            <td>{m.contact?.type || '\u2014'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className={classes.listPreviewFooter}>
                    <span className={classes.pageInfo}>
                      {listMemberMeta.total} member{listMemberMeta.total !== 1 ? 's' : ''}
                      {listMemberMeta.last_page > 1 && <>{' \u2014 '}Page {listMemberPage} of {listMemberMeta.last_page}</>}
                    </span>
                    {listMemberMeta.last_page > 1 && (
                      <div className={classes.pageButtons}>
                        <button
                          type="button"
                          className={classes.pageButton}
                          disabled={listMemberPage <= 1}
                          onClick={() => loadListMembers(form.contact_filter.list_id!, listMemberPage - 1)}
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          className={classes.pageButton}
                          disabled={listMemberPage >= listMemberMeta.last_page}
                          onClick={() => loadListMembers(form.contact_filter.list_id!, listMemberPage + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className={classes.formActions}>
        <button type="button" className={classes.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" className={classes.btnPrimary} disabled={saving}>
          {saving ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Campaign Detail
// ---------------------------------------------------------------------------

interface CampaignDetailProps {
  campaign: Campaign;
  contactLists: ContactList[];
  companyId: string;
  ssoToken: string;
  onBack: () => void;
  onEdit: () => void;
  onSend: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

function CampaignDetail({ campaign, contactLists, companyId, ssoToken, onBack, onEdit, onSend, onCancel, onDelete }: CampaignDetailProps) {
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [recipientPage, setRecipientPage] = useState(1);
  const [recipientMeta, setRecipientMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);

  const [replies, setReplies] = useState<CampaignReply[]>([]);
  const [replyPage, setReplyPage] = useState(1);
  const [replyMeta, setReplyMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadRecipients = useCallback(async (page: number) => {
    setLoadingRecipients(true);
    setRecipientError(null);
    try {
      const res = await api.listRecipients(companyId, campaign.id, ssoToken, page);
      if (!mountedRef.current) return;
      setRecipients(res.data);
      setRecipientMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setRecipientPage(page);
    } catch {
      if (!mountedRef.current) return;
      setRecipientError('Failed to load recipients');
    } finally {
      if (mountedRef.current) setLoadingRecipients(false);
    }
  }, [companyId, campaign.id, ssoToken]);

  const loadReplies = useCallback(async (page: number) => {
    setLoadingReplies(true);
    setReplyError(null);
    try {
      const res = await api.listReplies(companyId, campaign.id, ssoToken, page);
      if (!mountedRef.current) return;
      setReplies(res.data);
      setReplyMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setReplyPage(page);
    } catch {
      if (!mountedRef.current) return;
      setReplyError('Failed to load replies');
    } finally {
      if (mountedRef.current) setLoadingReplies(false);
    }
  }, [companyId, campaign.id, ssoToken]);

  useEffect(() => {
    if (campaign.status !== 'draft') {
      loadRecipients(1);
      loadReplies(1);
    }
  }, [campaign.status, loadRecipients, loadReplies]);

  const stats = campaign.stats;

  return (
    <div>
      <div className={classes.detailHeader}>
        <div>
          <div style={{ marginBottom: '0.5rem' }}>
            <button type="button" className={classes.btnSecondary} onClick={onBack} style={{ marginRight: '0.5rem' }}>
              &larr; Back
            </button>
          </div>
          <h2 className={classes.detailTitle}>{campaign.name}</h2>
          <div className={classes.detailMeta}>
            <span className={statusClass(campaign.status)}>{campaign.status}</span>
            <span className={channelClass(campaign.channel)}>{campaign.channel.toUpperCase()}</span>
            <span className={classes.metaItem}>Created {formatDate(campaign.created_at)}</span>
            {campaign.started_at && <span className={classes.metaItem}>Started {formatDate(campaign.started_at)}</span>}
            {campaign.completed_at && <span className={classes.metaItem}>Completed {formatDate(campaign.completed_at)}</span>}
          </div>
        </div>
        <div className={classes.btnGroup}>
          {campaign.status === 'draft' && (
            <>
              <button type="button" className={classes.btnSecondary} onClick={onEdit}>Edit</button>
              <button type="button" className={classes.btnSuccess} onClick={onSend}>Send</button>
              <button type="button" className={classes.btnDanger} onClick={onDelete}>Delete</button>
            </>
          )}
          {campaign.status === 'sending' && (
            <button type="button" className={classes.btnWarning} onClick={onCancel}>Cancel Sending</button>
          )}
        </div>
      </div>

      {/* Subject (email) */}
      {campaign.channel === 'email' && campaign.subject && (
        <div className={classes.bodyPreview}>
          <p className={classes.bodyPreviewLabel}>Subject</p>
          <div className={classes.bodyPreviewContent}>{campaign.subject}</div>
        </div>
      )}

      {/* Body */}
      <div className={classes.bodyPreview}>
        <p className={classes.bodyPreviewLabel}>Message Body</p>
        <div className={classes.bodyPreviewContent}>{campaign.body}</div>
      </div>

      {/* Contact Filter */}
      {campaign.contact_filter?.list_id && (
        <div className={classes.filterSection}>
          <p className={classes.filterLabel}>Recipient Filters</p>
          {(() => {
            const list = contactLists.find((cl) => cl.id === campaign.contact_filter.list_id);
            return (
              <span className={classes.metaItem} style={{ display: 'block', marginBottom: '0.25rem' }}>
                Contact list: <strong>{list ? `${list.name} (${list.members_count})` : `#${campaign.contact_filter.list_id}`}</strong>
              </span>
            );
          })()}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className={classes.statsRow}>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.total}</p>
            <p className={classes.statLabel}>Total</p>
          </div>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.pending}</p>
            <p className={classes.statLabel}>Pending</p>
          </div>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.sent}</p>
            <p className={classes.statLabel}>Sent</p>
          </div>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.delivered}</p>
            <p className={classes.statLabel}>Delivered</p>
          </div>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.failed}</p>
            <p className={classes.statLabel}>Failed</p>
          </div>
          <div className={classes.statCard}>
            <p className={classes.statValue}>{stats.bounced}</p>
            <p className={classes.statLabel}>Bounced</p>
          </div>
        </div>
      )}

      {/* Recipients Table */}
      {campaign.status !== 'draft' && (
        <div className={classes.card}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
              Recipients
              {recipientMeta.total > 0 && <span style={{ fontWeight: 400, color: '#6b7280' }}> ({recipientMeta.total})</span>}
            </h3>
          </div>
          {loadingRecipients ? (
            <div className={classes.loadingState}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span>Loading recipients...</span>
            </div>
          ) : recipientError ? (
            <div style={{ padding: '1rem' }}>
              <div className={classes.errorAlert}>{recipientError}</div>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => loadRecipients(recipientPage)}
              >
                Retry
              </button>
            </div>
          ) : recipients.length === 0 ? (
            <div className={classes.emptyState}>
              <p className={classes.emptyTitle}>No recipients yet</p>
              <p className={classes.emptyText}>Recipients will appear here once the campaign starts sending.</p>
            </div>
          ) : (
            <>
              <div className={classes.tableWrapper}>
                <table className={`table table-hover ${classes.table}`}>
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>{campaign.channel === 'email' ? 'Email' : 'Phone'}</th>
                      <th>Status</th>
                      <th>Sent At</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.id}>
                        <td>{r.contact?.display_name || r.contact_id}</td>
                        <td>{campaign.channel === 'email' ? r.contact?.email : r.contact?.phone}</td>
                        <td><span className={recipientStatusClass(r.status)}>{r.status}</span></td>
                        <td>{formatDate(r.sent_at)}</td>
                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.error_message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {recipientMeta.last_page > 1 && (
                <div className={classes.pagination}>
                  <span className={classes.pageInfo}>Page {recipientPage} of {recipientMeta.last_page}</span>
                  <div className={classes.pageButtons}>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={recipientPage <= 1}
                      onClick={() => loadRecipients(recipientPage - 1)}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={recipientPage >= recipientMeta.last_page}
                      onClick={() => loadRecipients(recipientPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Replies */}
      {campaign.status !== 'draft' && (
        <div className={classes.card} style={{ marginTop: '1.5rem' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
              Replies
              {replyMeta.total > 0 && <span style={{ fontWeight: 400, color: '#6b7280' }}> ({replyMeta.total})</span>}
            </h3>
          </div>
          {loadingReplies ? (
            <div className={classes.loadingState}>
              <div className="spinner-border spinner-border-sm text-primary" role="status" />
              <span>Loading replies...</span>
            </div>
          ) : replyError ? (
            <div style={{ padding: '1rem' }}>
              <div className={classes.errorAlert}>{replyError}</div>
              <button type="button" className={classes.btnSecondary} onClick={() => loadReplies(replyPage)}>Retry</button>
            </div>
          ) : replies.length === 0 ? (
            <div className={classes.emptyState}>
              <p className={classes.emptyTitle}>No replies yet</p>
              <p className={classes.emptyText}>Replies from recipients will appear here.</p>
            </div>
          ) : (
            <>
              <div className={classes.tableWrapper}>
                <table className={`table table-hover ${classes.table}`}>
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Reply</th>
                      <th>Type</th>
                      <th>Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {replies.map((r) => (
                      <tr key={r.id}>
                        <td>{r.contact?.display_name || r.contact_id}</td>
                        <td style={{ maxWidth: '400px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.body || '\u2014'}</td>
                        <td>{r.message_type || '\u2014'}</td>
                        <td>{formatDate(r.date_added)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {replyMeta.last_page > 1 && (
                <div className={classes.pagination}>
                  <span className={classes.pageInfo}>Page {replyPage} of {replyMeta.last_page}</span>
                  <div className={classes.pageButtons}>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={replyPage <= 1}
                      onClick={() => loadReplies(replyPage - 1)}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={replyPage >= replyMeta.last_page}
                      onClick={() => loadReplies(replyPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Campaigns Container
// ---------------------------------------------------------------------------

function CampaignsContainer({ ssoToken, companyId }: Props) {
  const [view, setView] = useState<View>('list');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignMeta, setCampaignMeta] = useState<{ last_page: number; total: number }>({ last_page: 1, total: 0 });
  const [contactLists, setContactLists] = useState<ContactList[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'send' | 'cancel' | 'delete'; campaign: Campaign } | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Load contact lists (for the form picker)
  const loadContactLists = useCallback(async () => {
    try {
      const res = await listContactLists(companyId, ssoToken);
      if (mountedRef.current) setContactLists(res.data);
    } catch {
      // non-blocking — form still works without lists
    }
  }, [companyId, ssoToken]);

  // Load campaigns list
  const loadCampaigns = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listCampaigns(companyId, ssoToken, page);
      if (!mountedRef.current) return;
      setCampaigns(res.data);
      setCampaignMeta({ last_page: res.meta.last_page, total: res.meta.total });
      setCampaignPage(page);
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load campaigns');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, ssoToken]);

  useEffect(() => {
    loadCampaigns();
    loadContactLists();
  }, [loadCampaigns, loadContactLists]);

  // Load single campaign detail
  const loadCampaignDetail = useCallback(async (campaignId: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getCampaign(companyId, campaignId, ssoToken);
      if (!mountedRef.current) return;
      setSelectedCampaign(res.data);
      setView('detail');
    } catch {
      if (!mountedRef.current) return;
      setError('Failed to load campaign');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, ssoToken]);

  // Create
  const handleCreate = useCallback(async (data: CampaignFormData) => {
    setSaving(true);
    setError(null);
    try {
      await api.createCampaign(companyId, data, ssoToken);
      if (!mountedRef.current) return;
      setView('list');
      await loadCampaigns();
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to create campaign');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [companyId, ssoToken, loadCampaigns]);

  // Update
  const handleUpdate = useCallback(async (data: CampaignFormData) => {
    if (!selectedCampaign) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.updateCampaign(companyId, selectedCampaign.id, data, ssoToken);
      if (!mountedRef.current) return;
      setSelectedCampaign(res.data);
      setView('detail');
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update campaign');
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [companyId, ssoToken, selectedCampaign]);

  // Confirm action handlers
  const handleConfirmAction = useCallback(async () => {
    if (!confirmAction) return;
    const { type, campaign } = confirmAction;
    setConfirmAction(null);
    setError(null);

    try {
      if (type === 'send') {
        await api.sendCampaign(companyId, campaign.id, ssoToken);
        if (!mountedRef.current) return;
        // Re-fetch the campaign to get updated status
        const refreshed = await api.getCampaign(companyId, campaign.id, ssoToken);
        if (!mountedRef.current) return;
        setSelectedCampaign(refreshed.data);
      } else if (type === 'cancel') {
        await api.cancelCampaign(companyId, campaign.id, ssoToken);
        if (!mountedRef.current) return;
        // Re-fetch the campaign to get updated status
        const refreshed = await api.getCampaign(companyId, campaign.id, ssoToken);
        if (!mountedRef.current) return;
        setSelectedCampaign(refreshed.data);
      } else if (type === 'delete') {
        await api.deleteCampaign(companyId, campaign.id, ssoToken);
        if (!mountedRef.current) return;
        setView('list');
        setSelectedCampaign(null);
        await loadCampaigns();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || `Failed to ${type} campaign`);
    }
  }, [confirmAction, companyId, ssoToken, loadCampaigns]);

  // Navigation handlers
  const goToList = useCallback(() => {
    setView('list');
    setSelectedCampaign(null);
    setError(null);
    loadCampaigns();
  }, [loadCampaigns]);

  const goToCreate = useCallback(() => {
    setView('create');
    setError(null);
  }, []);

  const goToEdit = useCallback(() => {
    setView('edit');
    setError(null);
  }, []);

  // Build form data from existing campaign for editing
  const editFormData = useMemo((): CampaignFormData | undefined => {
    if (!selectedCampaign) return undefined;
    return {
      name: selectedCampaign.name,
      channel: selectedCampaign.channel,
      subject: selectedCampaign.subject || '',
      body: selectedCampaign.body,
      html: selectedCampaign.html || '',
      contact_filter: selectedCampaign.contact_filter || {},
    };
  }, [selectedCampaign]);

  return (
    <div className={classes.container}>
      {error && <div className={classes.errorAlert}>{error}</div>}

      {/* Confirm Dialog */}
      {confirmAction && (
        <ConfirmDialog
          title={
            confirmAction.type === 'send' ? 'Send Campaign' :
            confirmAction.type === 'cancel' ? 'Cancel Campaign' :
            'Delete Campaign'
          }
          message={
            confirmAction.type === 'send'
              ? `Are you sure you want to send "${confirmAction.campaign.name}"? This will start delivering messages to all matching contacts.`
              : confirmAction.type === 'cancel'
              ? `Are you sure you want to cancel "${confirmAction.campaign.name}"? Messages that have already been sent cannot be unsent.`
              : `Are you sure you want to delete "${confirmAction.campaign.name}"? This action cannot be undone.`
          }
          confirmLabel={confirmAction.type === 'send' ? 'Send Now' : confirmAction.type === 'cancel' ? 'Cancel Campaign' : 'Delete'}
          confirmClass={confirmAction.type === 'send' ? classes.btnSuccess : classes.btnDanger}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>Campaigns</h2>
              <p className={classes.subtitle}>Create and manage email &amp; SMS campaigns</p>
            </div>
            <button type="button" className={classes.btnPrimary} onClick={goToCreate}>
              + New Campaign
            </button>
          </div>

          {loading ? (
            <div className={classes.loadingState}>
              <div className="spinner-border text-primary" role="status" />
              <span>Loading campaigns...</span>
            </div>
          ) : campaigns.length === 0 ? (
            <div className={classes.card}>
              <div className={classes.emptyState}>
                <p className={classes.emptyTitle}>No campaigns yet</p>
                <p className={classes.emptyText}>Create your first email or SMS campaign to get started.</p>
                <button type="button" className={classes.btnPrimary} onClick={goToCreate}>
                  + New Campaign
                </button>
              </div>
            </div>
          ) : (
            <div className={classes.card}>
              <div className={classes.tableWrapper}>
                <table className={`table table-hover ${classes.table}`}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Channel</th>
                      <th>Status</th>
                      <th>Recipients</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <button
                            type="button"
                            className={classes.campaignName}
                            onClick={() => loadCampaignDetail(c.id)}
                          >
                            {c.name}
                          </button>
                        </td>
                        <td><span className={channelClass(c.channel)}>{c.channel.toUpperCase()}</span></td>
                        <td><span className={statusClass(c.status)}>{c.status}</span></td>
                        <td>{c.stats?.total ?? '—'}</td>
                        <td>{formatDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {campaignMeta.last_page > 1 && (
                <div className={classes.pagination}>
                  <span className={classes.pageInfo}>Page {campaignPage} of {campaignMeta.last_page}</span>
                  <div className={classes.pageButtons}>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={campaignPage <= 1}
                      onClick={() => loadCampaigns(campaignPage - 1)}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      className={classes.pageButton}
                      disabled={campaignPage >= campaignMeta.last_page}
                      onClick={() => loadCampaigns(campaignPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* CREATE VIEW */}
      {view === 'create' && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>New Campaign</h2>
              <p className={classes.subtitle}>Create a new email or SMS campaign</p>
            </div>
          </div>
          <CampaignForm
            contactLists={contactLists}
            companyId={companyId}
            ssoToken={ssoToken}
            onSubmit={handleCreate}
            onCancel={goToList}
            submitLabel="Create Campaign"
            saving={saving}
          />
        </>
      )}

      {/* EDIT VIEW */}
      {view === 'edit' && selectedCampaign && (
        <>
          <div className={classes.header}>
            <div>
              <h2 className={classes.title}>Edit Campaign</h2>
              <p className={classes.subtitle}>{selectedCampaign.name}</p>
            </div>
          </div>
          <CampaignForm
            initial={editFormData}
            contactLists={contactLists}
            companyId={companyId}
            ssoToken={ssoToken}
            onSubmit={handleUpdate}
            onCancel={() => setView('detail')}
            submitLabel="Save Changes"
            saving={saving}
          />
        </>
      )}

      {/* DETAIL VIEW */}
      {view === 'detail' && selectedCampaign && !loading && (
        <CampaignDetail
          campaign={selectedCampaign}
          contactLists={contactLists}
          companyId={companyId}
          ssoToken={ssoToken}
          onBack={goToList}
          onEdit={goToEdit}
          onSend={() => setConfirmAction({ type: 'send', campaign: selectedCampaign })}
          onCancel={() => setConfirmAction({ type: 'cancel', campaign: selectedCampaign })}
          onDelete={() => setConfirmAction({ type: 'delete', campaign: selectedCampaign })}
        />
      )}

      {/* Detail loading */}
      {view === 'detail' && loading && (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status" />
          <span>Loading campaign...</span>
        </div>
      )}
    </div>
  );
}

const CampaignsContainerMemo = memo(CampaignsContainer, areEqual);

export { CampaignsContainerMemo as CampaignsContainer };
